'use client';

import {
    cloneElement,
    isValidElement,
    type CSSProperties,
    type ComponentPropsWithRef,
    type ComponentType,
    type ReactElement,
    type ReactNode,
    type SVGProps,
} from 'react';
import { Icon } from '@/shared-ui/atoms/icon';
import { Typography, type TypographyProps } from '@/shared-ui/atoms/typography';
import { cn } from '@/lib/utils';

type MenuItemIcon = ComponentType<SVGProps<SVGSVGElement>>;

export interface MenuItemProps extends Omit<ComponentPropsWithRef<'button'>, 'children'> {
    children: ReactNode;
    leftIcon?: MenuItemIcon;
    rightIcon?: MenuItemIcon;
    textAlign?: 'left' | 'center' | 'right';
    textWeight?: 'normal' | 'medium' | 'light';
    /** Typography variant for the menu label. Defaults to `h3`. */
    textVariant?: TypographyProps['variant'];
    /** Additional classes merged onto the label `Typography` (e.g. `text-body-sm`). */
    textClassName?: string;
    width?: number | string;
    backgroundColor?: string;
    hoverBackgroundColor?: string;
    showTopBorder?: boolean;
    showBottomBorder?: boolean;
    /** Icon slot width/height in pixels. Defaults to 16. */
    iconSizePx?: number;
    /** When true, removes horizontal padding (`px-0`) while keeping vertical padding. */
    removeHorizontalPadding?: boolean;
    /**
     * When true with a single side icon, reserves equal space on the opposite side so centered text
     * aligns to the full row width (as if an invisible icon were present).
     */
    balanceSideIcons?: boolean;
    /**
     * Render the provided child element (e.g. an anchor/`Link`) as the root instead of a `<button>`,
     * merging the menu item's styling onto it. The child's own children are used as the row label.
     */
    asChild?: boolean;
}

export const MenuItem = ({
    children,
    leftIcon,
    rightIcon,
    textAlign = 'center',
    textWeight = 'normal',
    textVariant = 'h3',
    textClassName,
    width = '100%',
    backgroundColor,
    hoverBackgroundColor,
    showTopBorder = false,
    showBottomBorder = false,
    iconSizePx = 16,
    removeHorizontalPadding = false,
    style,
    className,
    type = 'button',
    balanceSideIcons = false,
    asChild = false,
    ...props
}: MenuItemProps) => {
    const reserveLeftSlot = Boolean(leftIcon) || (balanceSideIcons && rightIcon);
    const reserveRightSlot = Boolean(rightIcon) || (balanceSideIcons && leftIcon);
    const iconSlotPx = iconSizePx;

    const gridTemplateColumns = reserveLeftSlot
        ? reserveRightSlot
            ? `${iconSlotPx}px 1fr ${iconSlotPx}px`
            : `${iconSlotPx}px 1fr`
        : reserveRightSlot
          ? `1fr ${iconSlotPx}px`
          : undefined;

    const rootClassName = cn(
        'grid w-full items-center gap-4 py-4 text-foreground transition-colors cursor-pointer disabled:cursor-not-allowed',
        removeHorizontalPadding ? 'px-0' : 'px-4',
        backgroundColor ? undefined : 'bg-background',
        hoverBackgroundColor ? 'hover:bg-[var(--menu-item-hover-bg)]' : 'hover:bg-accent',
        (showTopBorder || showBottomBorder) && 'border-border',
        showTopBorder && 'border-t',
        showBottomBorder && 'border-b',
        className
    );

    const rootStyle = {
        ...style,
        width,
        backgroundColor,
        '--menu-item-hover-bg': hoverBackgroundColor,
        ...(gridTemplateColumns ? { gridTemplateColumns } : {}),
    } as CSSProperties;

    const renderIconSlot = (icon: MenuItemIcon | undefined, slot: 'menu-item-left-icon' | 'menu-item-right-icon') => (
        <span
            data-slot={slot}
            className="inline-flex shrink-0 items-center justify-center"
            style={{ width: iconSlotPx, height: iconSlotPx }}
            aria-hidden="true">
            {icon ? <Icon icon={icon} className="h-full w-full" decorative /> : null}
        </span>
    );

    const renderInner = (label: ReactNode) => (
        <>
            {reserveLeftSlot ? renderIconSlot(leftIcon, 'menu-item-left-icon') : null}
            <Typography
                variant={textVariant}
                align={textAlign}
                weight={textWeight === 'medium' ? 'medium' : textWeight === 'light' ? 'light' : 'regular'}
                className={cn('w-full', textClassName)}>
                {label}
            </Typography>
            {reserveRightSlot ? renderIconSlot(rightIcon, 'menu-item-right-icon') : null}
        </>
    );

    if (asChild && isValidElement(children)) {
        const child = children as ReactElement<{ className?: string; style?: CSSProperties; children?: ReactNode }>;

        return cloneElement(
            child,
            {
                ...props,
                'data-slot': 'menu-item',
                className: cn(rootClassName, child.props.className),
                style: { ...rootStyle, ...child.props.style },
            },
            renderInner(child.props.children)
        );
    }

    return (
        <button data-slot="menu-item" type={type} className={rootClassName} style={rootStyle} {...props}>
            {renderInner(children)}
        </button>
    );
};
